/**
 * A reusable form field component..
 */
import {css, StyleSheet, type CSSProperties} from "aphrodite";
import {useId} from "react";

import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {a11yStyles} from "@repo/shared-styles";

import {FormError} from "./form-error.tsx";
import sharedStyles from "./shared-styles.ts";

const ceSharedStyles = StyleSheet.create(sharedStyles);

interface ValidatedFormTextareaProps {
    rows?: number;
    label?: string;
    description?: React.ReactNode;
    spellCheck?: boolean | "true" | "false";
    required?: boolean;
    errorMessage?: string;
    value?: string;
    placeholder?: string;
    onChange: (arg1?: any) => void;
    onBlur?: (arg1?: any) => void;
    onKeyDown?: (arg1?: any) => void;
    disabled?: boolean;
    style?: CSSProperties;
    testId?: string;
    labelIsSrOnly?: boolean;
}

function maybeRenderDescription(description?: React.ReactNode) {
    if (!description) {
        return null;
    }
    return (
        <>
            <Strut size={spacing.xxxSmall_4} />
            <LabelSmall>{description}</LabelSmall>
        </>
    );
}

const getBackgroundColor = (disabled: boolean, errored: boolean): string => {
    if (errored) {
        return "#ffe6e6";
    }
    if (disabled) {
        return color.offWhite;
    }
    return color.white;
};

export const ValidatedFormTextarea = (
    props: ValidatedFormTextareaProps,
): React.ReactElement => {
    const {
        rows,
        label,
        description,
        spellCheck,
        required,
        errorMessage,
        value,
        placeholder,
        onChange,
        onBlur,
        onKeyDown,
        disabled,
        style = {},
        testId = "",
        labelIsSrOnly = false,
    } = props;

    const id = useId();
    return (
        <>
            {label && (
                <LabelMedium
                    htmlFor={id}
                    style={labelIsSrOnly ? a11yStyles.srOnly : undefined}
                    testId="validated-form-textarea-label"
                >
                    {label}
                    {required && (
                        <span className={css(ceSharedStyles.redText)}> *</span>
                    )}
                </LabelMedium>
            )}
            {maybeRenderDescription(description)}
            {errorMessage && <FormError message={errorMessage} />}
            <Strut size={spacing.xSmall_8} />
            <textarea
                id={id}
                style={{
                    ...styles.textarea,
                    backgroundColor: getBackgroundColor(
                        !!disabled,
                        !!errorMessage,
                    ),
                    ...style,
                }}
                rows={rows || 4}
                spellCheck={spellCheck}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                disabled={!!disabled}
                data-testid={testId}
            />
        </>
    );
};

const styles = {
    textarea: {
        border: `1px solid ${color.offBlack50}`,
        borderRadius: "4px",
        fontFamily: "Lato, sans-serif",
        fontSize: "16px",
        marginBottom: 0,
        marginTop: "6px",
        padding: "12px",
        color: color.offBlack,
    },
} as const;
